/**
 * WebSocket 聊天工具类
 * 用于实时接收消息推送，替代轮询机制
 */

// 根据环境自动选择 WebSocket 地址
const isDev = process.env.NODE_ENV === 'development'
const DEFAULT_WS_URL = 'wss://hd.381611.xyz'

// 开发环境：使用 ws:// 协议
const DEV_WS_URL = isDev ? `ws://${window.location.hostname}:8081` : ''

// 生产环境：使用 wss:// 协议（HTTPS 对应的 WebSocket，不需要端口号）
const PROD_WS_URL = process.env.VUE_APP_WS_BASE_URL || DEFAULT_WS_URL

const WS_BASE_URL = isDev ? DEV_WS_URL : PROD_WS_URL

class ChatWebSocket {
  constructor() {
    this.ws = null
    this.agentAccount = null // 当前登录的客服账号
    this.subscribedSessions = new Set() // 已订阅的会话列表
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 10 // 增加重连次数
    this.reconnectDelay = 2000 // 缩短重连间隔
    this.heartbeatInterval = null
    this.pongTimeout = null // pong 超时检测
    this.lastPongTime = 0 // 上次收到 pong 的时间
    this.messageHandlers = []
    this.isConnecting = false
    this.isManualClose = false
    this.pendingSubscriptions = [] // 等待连接完成后订阅的会话
  }

  /**
   * 初始化全局 WebSocket 连接（登录后调用）
   * @param {string} agentAccount - 客服账号
   * @returns {Promise<void>}
   */
  init(agentAccount) {
    return new Promise((resolve, reject) => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // 已连接，更新客服账号
        this.agentAccount = agentAccount
        resolve()
        return
      }

      if (this.isConnecting) {
        // 正在连接中，等待连接完成（最多等待10秒）
        let waitTime = 0
        const checkConnection = setInterval(() => {
          waitTime += 100
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            clearInterval(checkConnection)
            this.agentAccount = agentAccount
            resolve()
          } else if (waitTime >= 10000) {
            // 超时
            clearInterval(checkConnection)
            reject(new Error('WebSocket 连接超时'))
          }
        }, 100)
        return
      }

      this.isConnecting = true
      this.isManualClose = false
      this.agentAccount = agentAccount

      // 构建 WebSocket URL，包含客服账号和 token
      let wsUrl = `${WS_BASE_URL}/ws/chat?agentAccount=${encodeURIComponent(agentAccount)}`
      const token = localStorage.getItem('token')
      if (token) {
        wsUrl += `&token=${encodeURIComponent(token)}`
      }
      console.log('WebSocket 全局连接中:', wsUrl.replace(/token=[^&]+/, 'token=***'))

      try {
        this.ws = new WebSocket(wsUrl)
      } catch (error) {
        this.isConnecting = false
        console.error('WebSocket 创建失败:', error)
        reject(error)
        return
      }

      this.ws.onopen = () => {
        console.log('WebSocket 全局连接成功')
        this.isConnecting = false
        const wasReconnect = this.reconnectAttempts > 0
        this.reconnectAttempts = 0
        this.startHeartbeat()

        // 处理待订阅的会话
        if (this.pendingSubscriptions.length > 0) {
          this.pendingSubscriptions.forEach(sessionId => {
            this.subscribe(sessionId)
          })
          this.pendingSubscriptions = []
        }

        // 重连成功后通知所有处理器，触发消息补拉（仅限重连，首次连接不触发）
        if (wasReconnect || this._isForceReconnect) {
          this._isForceReconnect = false
          this.messageHandlers.forEach(handler => {
            try {
              handler({ type: 'reconnected' })
            } catch (e) {
              console.error('重连通知处理器错误:', e)
            }
          })
        }

        resolve()
      }

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          this.handleMessage(data)
        } catch (e) {
          console.error('解析 WebSocket 消息失败:', e)
        }
      }

      this.ws.onerror = (error) => {
        console.error('WebSocket 错误:', error)
        this.isConnecting = false
      }

      this.ws.onclose = (event) => {
        console.log('WebSocket 连接关闭:', event.code, event.reason)
        this.isConnecting = false
        this.stopHeartbeat()

        // 非主动关闭，尝试重连
        if (!this.isManualClose && this.reconnectAttempts < this.maxReconnectAttempts) {
          // 清空旧的订阅记录，确保重连后能重新订阅
          const oldSessions = [...this.subscribedSessions]
          this.subscribedSessions.clear()
          // 把旧订阅加入待订阅列表，重连成功后自动重新订阅
          oldSessions.forEach(s => {
            if (!this.pendingSubscriptions.includes(s)) {
              this.pendingSubscriptions.push(s)
            }
          })

          this.reconnectAttempts++
          const delay = Math.min(this.reconnectDelay * this.reconnectAttempts, 15000) // 递增延迟，最长15秒
          console.log(`WebSocket 重连尝试 ${this.reconnectAttempts}/${this.maxReconnectAttempts}，${delay}ms 后重连`)
          setTimeout(() => {
            this.init(this.agentAccount).catch(console.error)
          }, delay)
        }
      }
    })
  }

  /**
   * 连接 WebSocket（兼容旧接口，内部调用 init 和 subscribe）
   * @param {string} sessionId - 聊天会话 ID
   * @param {string} agentAccount - 客服账号（可选）
   * @returns {Promise<void>}
   */
  async connect(sessionId, agentAccount = null) {
    // 如果提供了 agentAccount，先初始化连接
    if (agentAccount && agentAccount !== this.agentAccount) {
      await this.init(agentAccount)
    }

    // 如果没有连接，尝试用已保存的账号初始化
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      if (!agentAccount && !this.agentAccount) {
        // 没有账号信息，使用匿名连接（客户端聊天）
        return this.initAnonymous(sessionId)
      }
      await this.init(agentAccount || this.agentAccount)
    }

    // 订阅会话
    if (sessionId) {
      this.subscribe(sessionId)
    }
  }

  /**
   * 匿名连接（客户端聊天使用）
   * @param {string} sessionId - 会话 ID
   * @returns {Promise<void>}
   */
  initAnonymous(sessionId) {
    return new Promise((resolve, reject) => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.subscribe(sessionId)
        resolve()
        return
      }

      if (this.isConnecting) {
        // 正在连接中，等待连接完成（最多等待10秒）
        let waitTime = 0
        const checkConnection = setInterval(() => {
          waitTime += 100
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            clearInterval(checkConnection)
            this.subscribe(sessionId)
            resolve()
          } else if (waitTime >= 10000) {
            // 超时
            clearInterval(checkConnection)
            reject(new Error('WebSocket 连接超时'))
          }
        }, 100)
        return
      }

      this.isConnecting = true
      this.isManualClose = false

      const wsUrl = `${WS_BASE_URL}/ws/chat?sessionId=${encodeURIComponent(sessionId)}`
      console.log('WebSocket 匿名连接中:', wsUrl)

      try {
        this.ws = new WebSocket(wsUrl)
      } catch (error) {
        this.isConnecting = false
        console.error('WebSocket 创建失败:', error)
        reject(error)
        return
      }

      this.ws.onopen = () => {
        console.log('WebSocket 匿名连接成功')
        this.isConnecting = false
        this.reconnectAttempts = 0
        this.startHeartbeat()
        this.subscribe(sessionId)
        resolve()
      }

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          this.handleMessage(data)
        } catch (e) {
          console.error('解析 WebSocket 消息失败:', e)
        }
      }

      this.ws.onerror = (error) => {
        console.error('WebSocket 错误:', error)
        this.isConnecting = false
      }

      this.ws.onclose = (event) => {
        console.log('WebSocket 连接关闭:', event.code, event.reason)
        this.isConnecting = false
        this.stopHeartbeat()

        if (!this.isManualClose && this.reconnectAttempts < this.maxReconnectAttempts) {
          // 清空旧的订阅记录，确保重连后能重新订阅
          this.subscribedSessions.clear()

          this.reconnectAttempts++
          const delay = Math.min(this.reconnectDelay * this.reconnectAttempts, 15000)
          console.log(`WebSocket 重连尝试 ${this.reconnectAttempts}/${this.maxReconnectAttempts}，${delay}ms 后重连`)
          setTimeout(() => {
            this.initAnonymous(sessionId).catch(console.error)
          }, delay)
        }
      }
    })
  }

  /**
   * 订阅聊天会话
   * @param {string} sessionId - 会话 ID
   */
  subscribe(sessionId) {
    if (!sessionId) return

    // 如果正在连接中，加入待订阅列表
    if (this.isConnecting) {
      if (!this.pendingSubscriptions.includes(sessionId)) {
        this.pendingSubscriptions.push(sessionId)
      }
      return
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      // 避免重复订阅
      if (this.subscribedSessions.has(sessionId)) {
        console.log('会话已订阅，跳过:', sessionId)
        return
      }

      this.ws.send(JSON.stringify({
        action: 'subscribe',
        sessionId: sessionId
      }))
      this.subscribedSessions.add(sessionId)
      console.log('已订阅会话:', sessionId, '当前订阅数:', this.subscribedSessions.size)
    } else {
      console.warn('WebSocket 未连接，无法订阅会话:', sessionId)
      // 加入待订阅列表
      if (!this.pendingSubscriptions.includes(sessionId)) {
        this.pendingSubscriptions.push(sessionId)
      }
    }
  }

  /**
   * 取消订阅聊天会话
   * @param {string} sessionId - 会话 ID
   */
  unsubscribe(sessionId) {
    if (!sessionId) return

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        action: 'unsubscribe',
        sessionId: sessionId
      }))
      this.subscribedSessions.delete(sessionId)
      console.log('已取消订阅会话:', sessionId, '当前订阅数:', this.subscribedSessions.size)
    }
  }

  /**
   * 处理接收到的消息
   * @param {Object} data - 消息数据
   */
  handleMessage(data) {
    console.log('收到 WebSocket 消息:', data)

    if (data.type === 'pong') {
      // 心跳响应，更新最后收到 pong 的时间
      this.lastPongTime = Date.now()
      return
    }

    // 服务端通知账户已到期，强制退出
    if (data.type === 'expired') {
      console.warn('账户已到期，WebSocket 被服务端关闭')
      this.isManualClose = true // 阻止重连
      alert(data.msg || '您的账户已到期，请先充值续费后再使用')
      localStorage.removeItem('token')
      localStorage.removeItem('username')
      localStorage.removeItem('user')
      const currentPath = window.location.hash || window.location.pathname
      if (!currentPath.includes('/login')) {
        window.location.href = '/#/login'
        window.location.reload()
      }
      return
    }

    if (data.type === 'subscribed') {
      console.log('订阅确认:', data.sessionId)
      // 确保添加到已订阅列表
      if (data.sessionId) {
        this.subscribedSessions.add(data.sessionId)
      }
      return
    }

    if (data.type === 'unsubscribed') {
      console.log('取消订阅确认:', data.sessionId)
      if (data.sessionId) {
        this.subscribedSessions.delete(data.sessionId)
      }
      return
    }

    if (data.type === 'newMessage' || data.type === 'onlineStatus' || data.type === 'readStatusUpdate') {
      // 新消息、在线状态变化或已读状态更新，通知所有处理器
      this.messageHandlers.forEach(handler => {
        try {
          handler(data)
        } catch (e) {
          console.error('消息处理器错误:', e)
        }
      })
    }
  }

  /**
   * 添加消息处理器
   * @param {Function} handler - 处理函数，接收消息对象
   * @returns {Function} 取消注册的函数
   */
  onMessage(handler) {
    this.messageHandlers.push(handler)
    // 返回取消注册的函数
    return () => {
      const index = this.messageHandlers.indexOf(handler)
      if (index > -1) {
        this.messageHandlers.splice(index, 1)
      }
    }
  }

  /**
   * 启动心跳
   */
  startHeartbeat() {
    this.stopHeartbeat()
    this.lastPongTime = Date.now()
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // 检查上次 pong 是否超时（超过 45 秒没收到 pong 认为连接已死）
        if (Date.now() - this.lastPongTime > 45000) {
          console.warn('WebSocket pong 超时，主动断开重连')
          this.ws.close()
          return
        }
        this.ws.send(JSON.stringify({ action: 'ping' }))
      }
    }, 25000) // 25秒一次心跳，避免被中间件的60秒超时踢掉
  }

  /**
   * 停止心跳
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  /**
   * 关闭连接
   */
  close() {
    this.isManualClose = true
    this.stopHeartbeat()
    this.lastPongTime = 0
    this.messageHandlers = []
    this.subscribedSessions.clear()
    this.pendingSubscriptions = []
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    this.agentAccount = null
    console.log('WebSocket 已关闭')
  }

  /**
   * 检查连接状态（包含心跳健康检查）
   * @returns {boolean}
   */
  isConnected() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false
    // 如果超过 50 秒没收到 pong，认为连接实际上已死
    if (this.lastPongTime > 0 && Date.now() - this.lastPongTime > 50000) return false
    return true
  }

  /**
   * 强制重连（页面恢复可见时调用）
   */
  forceReconnect() {
    console.log('强制重连 WebSocket...')
    // 标记为强制重连，确保 onopen 后触发消息补拉
    this._isForceReconnect = true
    // 重置重连计数
    this.reconnectAttempts = 0
    // 清空旧订阅，确保重连后重新订阅
    const oldSessions = [...this.subscribedSessions]
    this.subscribedSessions.clear()
    oldSessions.forEach(s => {
      if (!this.pendingSubscriptions.includes(s)) {
        this.pendingSubscriptions.push(s)
      }
    })
    // 关闭旧连接
    this.stopHeartbeat()
    if (this.ws) {
      this.isManualClose = true // 先阻止自动重连
      this.ws.close()
      this.ws = null
    }
    this.isManualClose = false
    this.isConnecting = false
    // 用已保存的账号重新连接
    if (this.agentAccount) {
      this.init(this.agentAccount).catch(console.error)
    }
  }

  /**
   * 获取连接状态文本
   * @returns {string}
   */
  getStatusText() {
    if (!this.ws) return '未连接'
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'WebSocket 连接中...'
      case WebSocket.OPEN: return 'WebSocket 已连接'
      case WebSocket.CLOSING: return 'WebSocket 关闭中...'
      case WebSocket.CLOSED: return '使用轮询模式'
      default: return '未知状态'
    }
  }
}

// 导出单例实例
export const chatWebSocket = new ChatWebSocket()

// 也导出类，以便需要时创建新实例
export default ChatWebSocket
